// A-Backtest — walk-forward simulation using the EXACT same A-Signal logic
// used live. Zero AI. Lets Jarvis validate ANY timeframe/period instantly,
// without a TradingView round-trip — same numbers philosophy as
// JarvisSMC_Strategy.pine (Win Rate / Payoff / Expectancy / Profit Factor).
//
// FULL Pine trade management, not a simplified stand-in: T1 books
// t1_pct% at the structural target, the remainder's stop moves to
// breakeven, then ATR-trails as a runner — exactly what
// JarvisSMC_Strategy.pine's ⑧ Trade Management section does. An earlier
// version of this file used a simplified "first touch of SL-vs-Target
// wins" race, which understated real performance (a winner that pulls
// back through target before running further got scored as a plain win,
// throwing away everything the trail would have captured) — that version
// was removed entirely per Sir's explicit instruction to match the real
// script logic, not an approximation of it.
//
// No look-ahead: at simulated "now" = bar i, only candles[0..i] are passed
// into A-Signal — it can never see a future bar. This mirrors exactly how
// A-Structure's confirmed-pivot detection already works (a pivot at index
// i-swingLen only becomes "known" once bar i is reached).
// ═══════════════════════════════════════════════════════════════════════════

import { run as runSignal } from './A_Signal';
import { JARVIS_DNA } from '../../dna/jarvisDNA';
import { MemoryManager } from '../../brain/MemoryManager';
import { NS } from '../../brain/AgentRegistry';

// ── Ongoing monitoring: save every backtest run, compare to the last one ──
// This is what makes it "future मध्ये win rate कमी झाला तर कळेल" — not a
// one-shot check. Each run gets stored; the NEXT run on the same timeframe
// is automatically compared against it.
const HISTORY_KEY = (tf) => `backtest_history_${tf}`;

export function saveBacktestResult(timeframe, result) {
  if (!result.ok) return null;
  const history = MemoryManager.read(NS.TRADING, HISTORY_KEY(timeframe), []);
  const entry = {
    date: new Date().toISOString(),
    nTrades: result.nTrades, winRate: result.winRate,
    expectancy: result.expectancy, profitFactor: result.profitFactor,
  };
  const updated = [entry, ...history].slice(0, 30); // last 30 runs per TF
  MemoryManager.write(NS.TRADING, HISTORY_KEY(timeframe), updated);
  return updated;
}

// Compares the just-computed result to the MOST RECENT previously-saved
// run for the same timeframe (before this one was saved). Returns null if
// there's no prior run to compare to yet.
export function compareToLastBacktest(timeframe, currentResult) {
  if (!currentResult.ok || currentResult.expectancy == null) return null;
  const history = MemoryManager.read(NS.TRADING, HISTORY_KEY(timeframe), []);
  if (history.length === 0) return null;
  const prev = history[0];
  if (prev.expectancy == null) return null;

  const delta = +(currentResult.expectancy - prev.expectancy).toFixed(2);
  const status = delta <= -0.15 ? 'DEGRADED' : delta >= 0.15 ? 'IMPROVED' : 'STABLE';
  return {
    status, delta,
    prevDate: prev.date, prevExpectancy: prev.expectancy, prevWinRate: prev.winRate,
    warning: status === 'DEGRADED'
      ? `⚠️ ${timeframe} cha Expectancy ${prev.expectancy}R वरून ${currentResult.expectancy}R वर घसरला (${prev.date.slice(0,10)} च्या मागच्या check पासून). Live signals वर जास्त सावध राहा.`
      : null,
  };
}

export function getBacktestHistory(timeframe) {
  return MemoryManager.read(NS.TRADING, HISTORY_KEY(timeframe), []);
}

// ── Live-signal gate for UNVALIDATED timeframes (1m/3m/5m) ────────────────
// 15m/30m/45m/1h/4h are already Pine-backtested → always allowed.
// 1m/3m/5m start LOCKED — they only unlock for live signals once a saved
// backtest run on THIS EXACT timeframe shows a real edge. This is the
// "future मध्ये backtest result बघून trade करता येईल" mechanism: no code
// change needed later, it re-checks the saved history every time.
const ALWAYS_APPROVED = ['15m', '30m', '45m', '1h', '4h'];
const MIN_TRADES_FOR_LIVE = 10; // lower than the 30-trade "confident" bar —
// short TFs won't have 30 clean trades in a few months of Yahoo history yet,
// but 10 is enough to refuse an obviously-broken timeframe.

export function isTimeframeApprovedForLive(timeframe) {
  if (ALWAYS_APPROVED.includes(timeframe)) {
    return { approved: true, reason: `${timeframe} आधीच Pine वर validated आहे.` };
  }
  const history = getBacktestHistory(timeframe);
  if (history.length === 0) {
    return { approved: false, reason: `${timeframe} अजून backtest केलेला नाही — आधी "🔬 Backtest ${timeframe}" चालवा.` };
  }
  const latest = history[0];
  if (latest.nTrades < MIN_TRADES_FOR_LIVE) {
    return { approved: false, reason: `${timeframe}: फक्त ${latest.nTrades} trades मिळाले (किमान ${MIN_TRADES_FOR_LIVE} हवेत) — जास्त data कालावधीने backtest परत चालवा.` };
  }
  if (latest.expectancy == null || latest.expectancy <= 0) {
    return { approved: false, reason: `${timeframe}: शेवटचा backtest Expectancy ${latest.expectancy ?? '—'}R — positive नाही, live signal बंद.` };
  }
  return {
    approved: true,
    reason: `✅ ${timeframe} unlocked — शेवटचा backtest: ${latest.nTrades} trades, Expectancy ${latest.expectancy}R (${latest.date.slice(0, 10)}).`,
    lastResult: latest,
  };
}

// ── "Dynamic study" — automates exactly what Sir does by hand on Pine:
// try a range of ADX Threshold values, run the SAME walk-forward backtest
// for each, and report which ones actually clear a real trade count with a
// real edge. Born from a genuine observation (1m "looks better" when ADX
// visually clears ~30-32) — this turns that eyeball impression into an
// actual 30-trades-or-honestly-say-so answer instead of trusting 5 trades.
export async function sweepADXThreshold({ candles, thresholds = [20, 25, 30, 32, 35, 40, 45, 50], swingLen, htfBiasSeries = null }) {
  const rows = [];
  for (const t of thresholds) {
    const r = await run({ candles, swingLen, htfBiasSeries, adxOverride: t });
    rows.push({
      threshold: t,
      nTrades: r.ok ? r.nTrades : 0,
      winRate: r.ok ? r.winRate : null,
      expectancy: r.ok ? r.expectancy : null,
      profitFactor: r.ok ? r.profitFactor : null,
      enoughTrades: r.ok && r.nTrades >= MIN_TRADES_FOR_LIVE,
      error: r.ok ? null : r.error,
    });
  }

  // "Best" = highest expectancy among rows that at least clear the same
  // MIN_TRADES_FOR_LIVE bar the live gate uses — a higher-expectancy row
  // with only 2 trades is noise, not a finding.
  const eligible = rows.filter(r => r.enoughTrades && r.expectancy != null);
  const best = eligible.length ? eligible.reduce((a, b) => (b.expectancy > a.expectancy ? b : a)) : null;

  return {
    ok: true,
    rows,
    best,
    note: best
      ? `ADX Threshold ${best.threshold} सर्वात चांगला — ${best.nTrades} trades, Expectancy ${best.expectancy}R. तरीही 30+ trades नसतील तर अजून पक्का निष्कर्ष नाही.`
      : `कुठल्याही threshold ला किमान ${MIN_TRADES_FOR_LIVE} trades मिळाले नाहीत — या timeframe साठी उपलब्ध data (Yahoo Finance) खूप कमी आहे, निष्कर्ष काढता येत नाही.`,
    agent: 'A-Backtest',
    requiresAI: false,
    ts: Date.now(),
  };
}

// Wilder ATR series — one pass, used for the trailing-stop distance at
// every bar of an open trade (not just the entry bar).
function atrSeries(candles, period = 14) {
  const n = candles.length;
  const tr  = new Array(n).fill(null);
  const atr = new Array(n).fill(null);
  for (let i = 1; i < n; i++) {
    const c = candles[i], p = candles[i - 1];
    tr[i] = Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close));
  }
  if (n <= period) return atr;
  let sum = 0;
  for (let i = 1; i <= period; i++) sum += tr[i];
  atr[period] = sum / period;
  for (let i = period + 1; i < n; i++) atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  return atr;
}

export async function run({ candles = [], swingLen, htfBiasSeries = null, adxOverride = null }) {
  // Read from JARVIS_DNA here, not at module top-level — see A_Fibonacci.js
  // for why (crashed the whole app in production build otherwise).
  const V = JARVIS_DNA.validated_strategy;
  if (!V) {
    console.error('[A-Backtest] JARVIS_DNA.validated_strategy missing:', JARVIS_DNA);
    return { ok: false, error: 'Internal config error — validated_strategy DNA missing', agent: 'A-Backtest' };
  }
  const len = swingLen || V.structure.swing_length;
  const minBars = Math.max(len + 2, V.regime_filter.length + V.regime_filter.smoothing + 2);

  if (!candles || candles.length < minBars + 30) {
    return { ok: false, error: `Need at least ${minBars + 30} candles for a meaningful backtest (got ${candles?.length || 0})`, agent: 'A-Backtest' };
  }

  // ── FULL Pine management, not the old simplified SL-vs-Target race ──────
  // Matches JarvisSMC_Strategy.pine exactly: T1 books t1Pct% at the target,
  // remainder's stop moves to breakeven, then ATR-trails. This is the SAME
  // logic Jarvis is validated on — a plain "first touch wins" race
  // understates real performance because it throws away everything the
  // trail would have captured on a winner that pulls back through target
  // before running further.
  //
  // BUG FIXED: this used to read V.management (wrong — that key doesn't
  // exist at this level), silently falling back to the ?? defaults every
  // single time instead of ever reading the real DNA values. Correct path
  // is V.risk.management (see jarvisDNA.js trading.validated_strategy.risk).
  const T1_PCT         = V.risk.management?.t1_pct         ?? 50;
  const MOVE_TO_BE      = V.risk.management?.move_to_be     ?? true;
  const USE_TRAIL       = V.risk.management?.use_trail      ?? true;
  const TRAIL_ATR_MULT  = V.risk.management?.trail_atr_mult ?? 2.0;

  const atrArr = atrSeries(candles, 14);
  const trades = [];
  let openTrade = null;

  for (let i = minBars; i < candles.length; i++) {
    const bar = candles[i];
    const atr = atrArr[i];

    if (openTrade) {
      const t = openTrade;
      if (t.dir === 'BUY') {
        if (!t.t1Hit && bar.high >= t.target) {
          t.bookedR += ((t.target - t.entry) / t.riskAbs) * (T1_PCT / 100);
          t.t1Hit = true;
          if (MOVE_TO_BE) t.sl = Math.max(t.sl, t.entry);
        }
        if (t.t1Hit && USE_TRAIL && atr) t.sl = Math.max(t.sl, bar.close - atr * TRAIL_ATR_MULT);
        if (bar.low <= t.sl) {
          const remainingPct = t.t1Hit ? (100 - T1_PCT) / 100 : 1;
          t.rMultiple = t.bookedR + ((t.sl - t.entry) / t.riskAbs) * remainingPct;
          t.result = t.rMultiple > 0 ? 'WIN' : 'LOSS';
          t.exitDate = bar.date;
          trades.push(t); openTrade = null; continue;
        }
      } else {
        if (!t.t1Hit && bar.low <= t.target) {
          t.bookedR += ((t.entry - t.target) / t.riskAbs) * (T1_PCT / 100);
          t.t1Hit = true;
          if (MOVE_TO_BE) t.sl = Math.min(t.sl, t.entry);
        }
        if (t.t1Hit && USE_TRAIL && atr) t.sl = Math.min(t.sl, bar.close + atr * TRAIL_ATR_MULT);
        if (bar.high >= t.sl) {
          const remainingPct = t.t1Hit ? (100 - T1_PCT) / 100 : 1;
          t.rMultiple = t.bookedR + ((t.entry - t.sl) / t.riskAbs) * remainingPct;
          t.result = t.rMultiple > 0 ? 'WIN' : 'LOSS';
          t.exitDate = bar.date;
          trades.push(t); openTrade = null; continue;
        }
      }
      continue;   // still open, don't look for a new entry on the same bar
    }

    // Flat — check for a new signal using ONLY data up to (and including) bar i.
    const htfBias = htfBiasSeries ? htfBiasSeries[i] ?? null : null;
    const signal = await runSignal({ candles: candles.slice(0, i + 1), htfBias, adxOverride });
    if (signal.ok && signal.action !== 'WAIT') {
      const riskAbs = Math.abs(signal.entry - signal.sl);
      openTrade = {
        dir: signal.action, entry: signal.entry, sl: signal.sl, target: signal.target,
        riskAbs, bookedR: 0, t1Hit: false,
        entryDate: bar.date, entryIdx: i, rr: signal.rr,
      };
    }
  }

  // ── Aggregate stats — same formulas as JarvisSMC_Strategy.pine's table ──
  const closed = trades.filter(t => t.result);
  const wins   = closed.filter(t => t.result === 'WIN');
  const losses = closed.filter(t => t.result === 'LOSS');

  const nTrades  = closed.length;
  const winRate  = nTrades > 0 ? (wins.length / nTrades) * 100 : null;
  const avgWinR  = wins.length   > 0 ? wins.reduce((s, t) => s + t.rMultiple, 0) / wins.length : null;
  const avgLossR = losses.length > 0 ? Math.abs(losses.reduce((s, t) => s + t.rMultiple, 0) / losses.length) : null;
  const payoff   = (avgWinR != null && avgLossR > 0) ? avgWinR / avgLossR : null;
  const grossWinR  = wins.reduce((s, t) => s + t.rMultiple, 0);
  const grossLossR = Math.abs(losses.reduce((s, t) => s + t.rMultiple, 0));
  const profitFactor = grossLossR > 0 ? grossWinR / grossLossR : null;
  const expectancy = (winRate != null && payoff != null)
    ? (winRate / 100) * payoff - (1 - winRate / 100)
    : null;

  // Worst losing streak
  let curStreak = 0, worstStreak = 0;
  closed.forEach(t => {
    if (t.result === 'LOSS') { curStreak++; worstStreak = Math.max(worstStreak, curStreak); }
    else curStreak = 0;
  });

  let verdict = 'NOT_ENOUGH_TRADES';
  if (nTrades >= 30 && expectancy != null) {
    verdict = expectancy >= 0.20 ? 'EDGE_LIKELY' : expectancy > 0 ? 'WEAK_POSITIVE' : 'NO_EDGE';
  }

  return {
    ok: true,
    nTrades,
    wins: wins.length,
    losses: losses.length,
    winRate:      winRate      != null ? +winRate.toFixed(1)      : null,
    payoff:       payoff       != null ? +payoff.toFixed(2)       : null,
    profitFactor: profitFactor != null ? +profitFactor.toFixed(2) : null,
    expectancy:   expectancy   != null ? +expectancy.toFixed(2)   : null,
    worstLossStreak: worstStreak,
    verdict,
    verdictText: {
      NOT_ENOUGH_TRADES: `फक्त ${nTrades} trades — 30+ हवेत खात्रीशीर उत्तरासाठी.`,
      EDGE_LIKELY:  `✅ Edge दिसतोय (Expectancy ${expectancy?.toFixed(2)}R).`,
      WEAK_POSITIVE: `🟡 जेमतेम positive (${expectancy?.toFixed(2)}R) — निश्चित सांगता येणार नाही.`,
      NO_EDGE: `❌ Edge दिसत नाही (${expectancy?.toFixed(2)}R).`,
    }[verdict],
    trades: closed,
    note: `Full Pine management: T1 वर ${T1_PCT}% book${MOVE_TO_BE ? ', breakeven ला SL' : ''}${USE_TRAIL ? `, मग ${TRAIL_ATR_MULT}×ATR trail` : ''} — जुनी "simplified SL-vs-Target race" पद्धत काढून टाकली, हीच JarvisSMC_Strategy.pine ची खरी management आहे.`,
    agent: 'A-Backtest',
    requiresAI: false,
    ts: Date.now(),
  };
}
